/**
 * What the Control Center shows about an agent, as pure functions.
 *
 * Extracted from `ControlCenter.tsx` so it can be tested without rendering anything (ROADMAP §7).
 * The component's job is to put these values on the screen; the decisions are here.
 *
 * `styleFor` carries a hard rule. CLAUDE.md §3.3: agent status is COMPUTED from run records, no
 * UI code may set it, and **if the run record does not support a state, the interface must not
 * show that state**. So an unrecognised state passes through as itself and is never busy —
 * nothing pulses without a run.
 */

export interface StateStyle {
  label: string;
  tone: string;
  /** Drives the pulse on the graph. True only while work is actually in flight. */
  busy: boolean;
}

/** Colour and label per state. Every state here is one `derived.agent_status` can actually produce. */
export const STATE_STYLE: Record<string, StateStyle> = {
  OFFLINE: { label: 'Offline', tone: '#4b5563', busy: false },
  IDLE: { label: 'Idle', tone: '#334155', busy: false },
  QUEUED: { label: 'Queued', tone: '#0369a1', busy: true },
  RETRIEVING_DATA: { label: 'Retrieving data', tone: '#0284c7', busy: true },
  ANALYZING: { label: 'Analyzing', tone: '#7c3aed', busy: true },
  CALLING_TOOL: { label: 'Calling a tool', tone: '#c2410c', busy: true },
  CALCULATING: { label: 'Calculating', tone: '#0d9488', busy: true },
  // Deliberately NOT busy: the agent is doing nothing and the owner is holding the work. An
  // animation here would say the system is still thinking.
  WAITING_FOR_APPROVAL: { label: 'Waiting for you', tone: '#b45309', busy: false },
  EXECUTING_APPROVED_ACTION: { label: 'Executing approved action', tone: '#b91c1c', busy: true },
  COLLABORATING: { label: 'Collaborating', tone: '#4f46e5', busy: true },
  COMPLETED: { label: 'Completed', tone: '#15803d', busy: false },
  FAILED: { label: 'Failed', tone: '#991b1b', busy: false },
};

export function styleFor(state: string): StateStyle {
  // An unknown state is shown as itself rather than mapped to something friendlier. If the view
  // grows a state this file does not know, saying so is better than guessing.
  return STATE_STYLE[state] ?? { label: state, tone: '#4b5563', busy: false };
}

/** Indexed by `autonomy_level`, so the order is the meaning. CLAUDE.md §3.5. */
export const AUTONOMY_LABEL = [
  '0 — Read only',
  '1 — Draft / recommend',
  '2 — Propose actions',
  '3 — Limited autonomy',
];

/**
 * How long the run in flight has been running, for the table.
 *
 * Clamped at zero: the timestamp comes from the database and `Date.now()` from the browser, so a
 * few seconds of skew is ordinary, and "-3s" on the screen reads as a platform bug.
 */
export function elapsed(since: string | null): string {
  if (!since) return '';
  const seconds = Math.max(0, Math.round((Date.now() - new Date(since).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

/** Positions agents evenly around the brain, starting at the top. Deterministic. */
export function layout(count: number, radius: number): { x: number; y: number }[] {
  if (count === 1) return [{ x: 0, y: -radius }];
  return Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
    return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
  });
}
